import { useParams } from '@fs/zion-router'
import { LabelTooltip, H3, H2 } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../app/PageTitleContext'
import useRequest from '../../hooks/useRequest'
import DmcCopyToClipboardTooltip from '../../util/DmcCopyToClipboardTooltip'
import { getBooleanDisplayValue, getIdElement, getLastPathElement, getNumericalIdFromId } from '../../util/StringUtil'
import { OwsLink, UserLink, PersonLink } from '../app/Links'
import DmcDataView from '../common/DmcDataView'

export default function Request() {
  usePageView({ page_detail: 'DMConsole:Request' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.request.base'))
  const { requestId } = useParams()
  const request = useRequest(requestId)

  if (request === null) {
    return <>{t('temple.request.lookupRequest', 'Look up a Request to view Request data')}</>
  }
  if (request === undefined) {
    return null
  }
  return (
    <>
      <H2 size="H5">{t('request', 'Request')}</H2>
      <RequestSummaryData request={request} />
      {request.ordinanceWorkSets.map((ows) => (
        <RequestOrdinanceWorkSet ows={ows} persons={request.persons} key={ows.id} />
      ))}
    </>
  )
}

function RequestSummaryData({ request }) {
  const { t } = useTranslation()
  const requestData = request.requests[0]
  const userData = request.agents && request.agents.length > 0 && request.agents[0]
  const requestSummaryData = [
    {
      label: <b>{t('temple.request.requestId', 'Request ID')}</b>,
      detail: `${requestData.id} (${getNumericalIdFromId(requestData.id)})`,
      key: `requestId-${requestData.id}`,
    },
    {
      label: <b>{t('created', 'Created')}</b>,
      detail: requestData.createDate.original,
      key: `created-${requestData.id}`,
    },
    {
      label: <b>{t('temple.request.isEmergencyList', 'Is Emergency List')}</b>,
      detail: getBooleanDisplayValue(requestData.isEmergencyList),
      key: `isEmergencyList-${requestData.id}`,
    },
    {
      label: <b>{t('temple.request.requestType', 'Request Type')}</b>,
      detail: getIdElement(requestData['@class']),
      key: `requestType-${requestData.id}`,
    },
    {
      label: <b>{t('temple.request.hasUnprinted', 'Has Unprinted')}</b>,
      detail: getBooleanDisplayValue(requestData.hasUnprinted),
      key: `hasUnprinted-${requestData.id}`,
    },
    {
      label: <b>{t('templeCode', 'Temple Code')}</b>,
      detail: requestData.templeRef && requestData.templeRef.templeCode ? requestData.templeRef.templeCode : null,
      key: `templeCode-${requestData.id}`,
    },
    {
      label: <b>{t('osid', 'OSID')}</b>,
      detail: requestData.osid,
      key: `osid-${requestData.id}`,
    },
    {
      label: <b>{t('temple.request.unrecordedCount', 'Unrecorded Count')}</b>,
      detail: requestData.unrecordedCount,
      key: `unrecordedCount-${requestData.id}`,
    },

    {
      label: <b>{t('temple.request.printDate', 'Print Date')}</b>,
      detail: requestData.printedOn && requestData.printedOn.original ? requestData.printedOn.original : null,
      key: `printedOn-${requestData.id}`,
    },
  ]

  if (userData) {
    requestSummaryData.push({
      label: <b>{t('userId', 'User ID')}</b>,
      detail: <UserLink userId={userData.id} />,
      key: `userId-${userData.id}`,
    })
    requestSummaryData.push({
      label: <b>{t('name', 'Name')}</b>,
      detail: userData.names && userData.names.length > 0 ? userData.names[0].value : null,
      key: `userName-${userData.id}`,
    })
  }

  return <DmcDataView data={requestSummaryData} />
}

function getNameLink(pid, personName, t) {
  if (pid === null) {
    return (
      <LabelTooltip target={personName} label={t('request.pidNotFoundInOws', 'PID not found in ordinance work set')} />
    )
  }
  return <LabelTooltip target={<PersonLink personId={pid} personName={personName} newTab />} label={pid} />
}

const getPidFromWorkSetPerson = (person) => {
  const pidIdentifier = person.identifiers.find((identifier) => identifier?.substring(0, 2) === 'p.')
  if (pidIdentifier) {
    return pidIdentifier?.substring(2)
  }
  return null
}

function RequestOrdinanceWorkSet({ ows, persons }) {
  const { t } = useTranslation()
  const getPersonNamesInvolvedInOrdinanceWorkSet = () => {
    // filter out persons with prefix of qr
    const personsInvolvedInOrdinanceWorkSet = persons.filter((person) => !person.id.includes('qr.'))
    // add principal names
    const principal =
      ows.principal && personsInvolvedInOrdinanceWorkSet.find((person) => person.id === ows.principal.resourceId)
    const personNamesInvolvedInOrdinanceWorkSet = []
    const principalNames = principal?.names?.[0]?.nameForms.map((nameForm) => {
      return {
        label: <b>{t('temple.request.principalAndLang', 'Principal ({lang})', { lang: nameForm.lang })}</b>,
        detail: getNameLink(getPidFromWorkSetPerson(principal), nameForm.fullText, t),
        key: `principalName-${ows.id}-${nameForm.lang}`,
      }
    })
    if (principalNames && principalNames.length > 0) {
      personNamesInvolvedInOrdinanceWorkSet.push(...principalNames)
    }
    // add wife names
    if (ows.wife) {
      const wife = personsInvolvedInOrdinanceWorkSet.find((person) => person.id === ows.wife.resourceId)
      const wifeNames = wife?.names?.[0]?.nameForms.map((nameForm) => {
        return {
          label: <b>{t('temple.request.wifeAndLang', 'Wife ({lang})', { lang: nameForm.lang })}</b>,
          detail: getNameLink(getPidFromWorkSetPerson(wife), nameForm.fullText, t),
          key: `wifeName-${ows.id}-${nameForm.lang}`,
        }
      })
      if (wifeNames && wifeNames.length > 0) {
        personNamesInvolvedInOrdinanceWorkSet.push(...wifeNames)
      }
    }
    // add father names
    if (ows.father) {
      const father = personsInvolvedInOrdinanceWorkSet.find((person) => person.id === ows.father.resourceId)
      const fatherNames = father?.names?.[0]?.nameForms.map((nameForm) => {
        return {
          label: <b>{t('temple.request.fatherAndLang', 'Father ({lang})', { lang: nameForm.lang })}</b>,
          detail: getNameLink(getPidFromWorkSetPerson(father), nameForm.fullText, t),
          key: `fatherName-${ows.id}-${nameForm.lang}`,
        }
      })
      if (fatherNames && fatherNames.length > 0) {
        personNamesInvolvedInOrdinanceWorkSet.push(...fatherNames)
      }
    }
    // add mother names
    if (ows.mother) {
      const mother = personsInvolvedInOrdinanceWorkSet.find((person) => person.id === ows.mother.resourceId)
      const motherNames = mother?.names?.[0]?.nameForms.map((nameForm) => {
        return {
          label: <b>{t('temple.request.motherAndLang', 'Mother ({lang})', { lang: nameForm.lang })}</b>,
          detail: getNameLink(getPidFromWorkSetPerson(mother), nameForm.fullText, t),
          key: `motherName-${ows.id}-${nameForm.lang}`,
        }
      })
      if (motherNames && motherNames.length > 0) {
        personNamesInvolvedInOrdinanceWorkSet.push(...motherNames)
      }
    }
    return personNamesInvolvedInOrdinanceWorkSet
  }

  const owsSummaryData = [
    {
      label: <b>{t('ows.owsId', 'OWS ID')}</b>,
      detail: (
        <>
          <OwsLink owsId={ows.id} />
          {' ('}
          <DmcCopyToClipboardTooltip value={getNumericalIdFromId(ows.id)} />
          {') '}
        </>
      ),
      key: `owsId-${ows.id}`,
    },
    {
      label: <b>{t('osid', 'OSID')}</b>,
      detail: ows.osid,
      key: `osid-${ows.id}`,
    },
    {
      label: <b>{t('printed', 'Printed')}</b>,
      detail: getBooleanDisplayValue(ows.beenPrinted),
      key: `printed-${ows.id}`,
    },
    {
      label: <b>{t('temple.request.paperType', 'Paper Type')}</b>,
      detail: getLastPathElement(ows.paperType),
      key: `paperType-${ows.id}`,
    },
    {
      label: <b>{t('created', 'Created')}</b>,
      detail: ows.createDate && ows.createDate.original && ows.createDate.original,
      key: `created-${ows.id}`,
    },
    {
      label: <b>{t('ows.submissionType', 'Submission Type')}</b>,
      detail: getLastPathElement(ows.submissionType),
      key: `submissionType-${ows.id}`,
    },
    {
      label: <b>{t('updated', 'Updated')}</b>,
      detail: ows.updateDate && ows.updateDate.original && ows.updateDate.original,
      key: `updated-${ows.id}`,
    },
    {
      label: <b>{t('duplicate', 'Duplicate')}</b>,
      detail: getBooleanDisplayValue(ows.duplicate),
      key: `duplicate-${ows.id}`,
    },
    {
      label: <b>{t('temple.request.deleteDate', 'Delete Date')}</b>,
      detail: ows.deletedDate?.original,
      key: `deleteDate-${ows.id}`,
    },
    ...getPersonNamesInvolvedInOrdinanceWorkSet(),
  ]
  const qualifiedOrdinanceData = [
    {
      label: <b>{t('ows.qualifiedOrdinances', 'Qualified Ordinances')}</b>,
      detail: qualifiedOrdinancesToString(ows.qualifiedOrdinances),
      key: `qualifiedOrdinances-${ows.id}`,
    },
  ]
  const printDateData = []
  ows.printDates?.forEach((printDate) => {
    printDateData.push({
      label: <b>{t('temple.request.printDate', 'Print Date')}</b>,
      detail: printDate.date?.original,
      key: `printDate-${ows.id}-${printDate.date?.original}`,
    })
    printDateData.push({
      label: <b>{t('temple.label', 'Temple')}</b>,
      detail: printDate.templeRef?.templeCode,
      key: `printDateTemple-${ows.id}-${printDate.date?.original}`,
    })
    printDateData.push({
      label: null,
      detail: null,
      key: `printDateSpacer-${ows.id}-${printDate.date?.original}`,
    })
  })
  return (
    <>
      <H3 size="H5" supplementary>
        {t('ows.ordinanceWorkSet', 'Ordinance Work Set')}
      </H3>
      <DmcDataView data={owsSummaryData} />
      <DmcDataView data={qualifiedOrdinanceData} />
      {ows.printDates && <DmcDataView data={printDateData} />}
    </>
  )
}

function qualifiedOrdinancesToString(qualifiedOrdinances) {
  const qualifiedOrdinancesOptions = [
    'BAPTISM_LDS',
    'CONFIRMATION_LDS',
    'INITIATORY',
    'ENDOWMENT',
    'SEALING_TO_PARENTS',
    'SEALING_TO_SPOUSE',
    'LDS_ORDINATION',
    'LDS_LICENSE_MARRIAGE',
    'TIME_ONLY_MARRIAGE',
  ]
  const owsQualifiedOrdinances = qualifiedOrdinances.map((owsQualifiedOrdinance) => {
    return getLastPathElement(owsQualifiedOrdinance)
  })
  let qualifiedOrdinancesString = ''
  qualifiedOrdinancesOptions.forEach((qualifiedOrdinanceOption) => {
    if (owsQualifiedOrdinances.includes(qualifiedOrdinanceOption)) {
      qualifiedOrdinancesString = qualifiedOrdinancesString.concat(`${qualifiedOrdinanceOption}, `)
    }
  })
  return qualifiedOrdinancesString.slice(0, -2)
}
